import React, { useContext, useEffect, useRef, useState } from 'react'
import Layout from '../components/pageComponents/layout'
import { graphql } from 'gatsby'
import { useProductI18n } from '../hooks/i18n/useProductI18n'
import { useTechnicalDetailsI18n } from '../hooks/i18n/useTechnicalDetailsI18n'
import { ProductSidebox } from '../components/productPrice/productSidebox'
import { getPriceForDetailPage } from '../services/backendApiService'
import { v4 as uuid } from 'uuid'
import Breadcrumb from '../components/breadcrumb'
import { useCountries } from '../hooks/useCountries'
import { isAnonymousUser } from '../services/userService'
import UserContext from '../contexts/userContext'
import GeolocationContext from '../contexts/geolocationContext'
import { logException } from '../services/loggingService'
import useSeoHrefLang from '../hooks/useSeoHrefLang'
import { getI18nText } from '../services/i18nService'
import TechnicalDetails from '../components/productDetail/technicalDetails'
import Schematics from '../components/productDetail/schematics'
import Downloads from '../components/productDetail/downloads'
import Navigation from '../components/productDetail/navigation'
import Cad from '../components/productDetail/cad'
import { usePriorityTechnicalDetailsI18n } from '../hooks/i18n/usePriorityTechnicalDetailsI18n'
import { getImage } from 'gatsby-plugin-image'
import ProductImageSwiper from '../components/productDetail/productImageSwiper'
import {
  getPriorityTechnicalDetails,
  getTechnicalDetails,
} from '../services/productPageMicrocopyService'
import useHideFromSpiders from '../hooks/useHideFromSpiders'
import { GetCurrencyForCountry } from '../services/currencyService'
import Accessories from '../components/productDetail/accessories'
import LanguageContext from '../contexts/languageContext'
import {
  buildViewItemDetailsEvent,
  publishViewItemDetailsEvent,
} from '../services/dataLayerService'
import BasketContext from '../contexts/basketContext'
import ProductRequestAQuoteModal from '../components/modal/productRequestAQuoteModal'
import P65 from '../components/productDetail/p65'
import BaseModal from '../components/modal/baseModal'
import {
  replaceCharacterWithUnicodeCode,
  replaceHtmlEntityWithCharacter,
} from '../services/htmlEncodingService'
import { defaultPricingDisplayOptions } from '../services/backendApiService'
import { useRequestQuoteI18n } from '../hooks/i18n/useRequestAQuoteI18n'
import Seo from '../components/pageComponents/seo'
import DataLayerContext from '../contexts/dataLayerContext'

const ProductPage = (props) => {
  const { eventApi } = useContext(DataLayerContext)
  const { email, queryingUser, accountNumber, isDistributor, country } = useContext(UserContext)
  const { country: geolocationCountry } = useContext(GeolocationContext)
  const { language } = useContext(LanguageContext)
  const { basketCurrency } = useContext(BasketContext)
  const countries = useCountries(props.pageContext.locale)
  const [data] = useState(extract(props.data.allContentfulProduct, props.pageContext.locale))
  const [isRange, setIsRange] = useState(false)
  const microcopy = useProductI18n(props.pageContext.locale)
  const requestAQuoteMicrocopy = useRequestQuoteI18n(props.pageContext.locale)
  const technicalDetailsMicrocopy = useTechnicalDetailsI18n(props.pageContext.locale)
  const priorityTechnicalDetailsMicrocopy = usePriorityTechnicalDetailsI18n(
    props.pageContext.locale
  )
  const [quantity, setQuantity] = useState(1)
  const [loading, setLoading] = useState(true)
  const [requestAQuoteIsOpen, setRequestAQuoteIsOpen] = useState(false)
  const [displayOptions, setDisplayOptions] = useState(defaultPricingDisplayOptions)
  const [product, setProduct] = useState()
  const [productDescription, setProductDescription] = useState(null)
  const [productImageUrl, setProductImageUrl] = useState(null)
  const [technicalDetails, setTechnicalDetails] = useState(null)
  const [priorityTechnicalDetails, setPriorityTechnicalDetails] = useState(null)
  const [itemFeatures, setItemFeatures] = useState(null)
  const [productImages, setProductImages] = useState([])
  const [showP65Warning, setShowP65Warning] = useState(false)
  const [eventModelToPublish, setEventModelToPublish] = useState()

  const getTheProductRequestIdRef = useRef(null)

  useEffect(() => {
    let myShowFindDistributor = false

    function getTheProduct(partNumber, theCurrency) {
      setLoading(true)
      getTheProductRequestIdRef.current = uuid()
      let quantityForEvent = 1
      let priceForEvent = undefined

      if (isRange === false) {
        getPriceForDetailPage(
          partNumber,
          geolocationCountry,
          theCurrency,
          language,
          getTheProductRequestIdRef.current
        )
          .then((responseData) => {
            if (responseData?.product) {
              setDisplayOptions({
                ...responseData.product.priceDisplayOptions,
                showFindDistributor: myShowFindDistributor,
              })
              setProduct(responseData.product)

              if (responseData.product.boxQuantity) {
                setQuantity(responseData.product.stockModel.boxQuantity)
                quantityForEvent = responseData.product.boxQuantity
              }
            } else {
              setQuantity(1)
              setDisplayOptions({
                ...defaultPricingDisplayOptions,
                showFindDistributor: myShowFindDistributor,
              })
            }
            setLoading(false)
            getTheProductRequestIdRef.current = null

            try {
              const desc =
                data.pimData && data.pimData.description
                  ? replaceHtmlEntityWithCharacter(data.pimData.description)
                  : undefined
              setEventModelToPublish(
                buildViewItemDetailsEvent(
                  basketCurrency,
                  priceForEvent,
                  undefined,
                  data.partNumber,
                  desc,
                  quantityForEvent,
                  data.pimData && data.pimData.brand ? data.pimData.brand[0] : undefined,
                  props.pageContext.locale,
                  accountNumber,
                  isDistributor
                )
              )
            } catch (error) {
              logException('buildViewItemDetailsEvent error', error)
            }
          })
          .catch((error) => {
            logException('Product page error', error)
            setQuantity(1)
            setDisplayOptions({
              ...defaultPricingDisplayOptions,
              showFindDistributor: myShowFindDistributor,
            })
            setLoading(false)
            getTheProductRequestIdRef.current = null
          })
      } else {
        setLoading(false)
        setDisplayOptions({
          ...defaultPricingDisplayOptions,
          showFindDistributor: myShowFindDistributor,
        })
        getTheProductRequestIdRef.current = null
      }
    }

    if (queryingUser === false) {
      if (isAnonymousUser(email) === true && geolocationCountry) {
        if (isRange === false) {
          myShowFindDistributor = shouldShowFindADistributor(geolocationCountry)
        }
        setShowP65Warning(shouldShowP65Warning(geolocationCountry))

        if (getTheProductRequestIdRef.current === null) {
          getTheProduct(data.partNumber, GetCurrencyForCountry(geolocationCountry, countries))
        }
      } else if (isAnonymousUser(email) === false) {
        if (isRange === false) {
          myShowFindDistributor = shouldShowFindADistributor(country)
        }
        setShowP65Warning(shouldShowP65Warning(country))

        if (getTheProductRequestIdRef.current === null) {
          getTheProduct(data.partNumber, null)
        }
      }
    }
  }, [data.partNumber, queryingUser, email, geolocationCountry, isRange, getTheProductRequestIdRef])

  useEffect(() => {
    setIsRange(data?.pimData?.israngesku === 'True')
  }, [data])

  useEffect(() => {
    if (data.pimData) {
      if (data.pimData.description) {
        setProductDescription(replaceHtmlEntityWithCharacter(data.pimData.description))
      } else {
        setProductDescription(null)
      }

      if (data.pimData.largeimage) {
        setProductImageUrl(`https://cdn.norgren.com/images/${data.pimData.largeimage}`)
      } else {
        setProductImageUrl(null)
      }

      if (data.pimData) {
        setTechnicalDetails(
          extractAllItemsFromDictionary(
            getTechnicalDetails(data.pimData, technicalDetailsMicrocopy)
          )
        )

        setPriorityTechnicalDetails(
          extractItemsFromDictionary(
            getPriorityTechnicalDetails(data.pimData, priorityTechnicalDetailsMicrocopy),
            5
          )
        )

        if (data.pimData.features) {
          setItemFeatures(data.pimData.features.split('|'))
        } else {
          setItemFeatures(null)
        }
      } else {
        setTechnicalDetails(null)
        setPriorityTechnicalDetails(null)
        setItemFeatures(null)
      }
    }
  }, [data.pimData])

  useEffect(() => {
    let productImages = []
    if (data.localImage) {
      data.localImage.forEach((element) => {
        productImages.push(getImage(element.childImageSharp.gatsbyImageData))
      })
    }
    setProductImages(productImages)
  }, [data.localImage])

  useEffect(() => {
    if (eventApi && eventModelToPublish) {
      publishViewItemDetailsEvent(eventApi, eventModelToPublish)
    }
  }, [eventApi, eventModelToPublish])

  function openRequestAQuoteModal() {
    setRequestAQuoteIsOpen(true)
  }

  function closeRequestAQuoteModal() {
    setRequestAQuoteIsOpen(false)
  }

  function shouldShowFindADistributor(currentCountry) {
    if (currentCountry && currentCountry.toLowerCase() === 'us') {
      return true
    }
    return false
  }

  function shouldShowP65Warning(currentCountry) {
    if (currentCountry && currentCountry.toLowerCase() === 'us') {
      return true
    }
    return false
  }

  return (
    <Layout
      locale={props.pageContext.locale}
      altPaths={props.pageContext.altPaths}
      categoryPaths={props.pageContext.categoryPaths}
      darkTheme={true}
      id={props.pageContext.navId}
    >
      <section className="product-details has-basket pb-0">
        <div className="container">
          <div className="row">
            <div className="col-15 col-md">
              <p className="product-details__item--number font__bold">{data.partNumber}</p>
              <h2 className="font__heavy mt-1 mb-0">{productDescription}</h2>
              <Navigation microcopy={microcopy} data={data.pimData} />
              <div className="row product-details__listing pb-5">
                <div className="col-15 col-lg-5 mb-5 mb-lg-0">
                  {productImages && productImages.length > 0 ? (
                    <ProductImageSwiper images={productImages}></ProductImageSwiper>
                  ) : productImageUrl ? (
                    <img src={productImageUrl} alt={data.partNumber}></img>
                  ) : null}
                </div>
                <div className="col-15 col-lg-10 ps-md-5">
                  <div className="product-details__zoom"></div>
                  {itemFeatures && itemFeatures.length > 0 ? (
                    <ul className="product-details__spec--features no-bullets">
                      {itemFeatures.map((feature, index) => {
                        if (feature !== '') {
                          return <li key={index}>{feature}</li>
                        } else {
                          return null
                        }
                      })}
                    </ul>
                  ) : null}
                  <table>
                    <tbody>
                      {priorityTechnicalDetails !== null &&
                        priorityTechnicalDetails !== undefined &&
                        Object.keys(priorityTechnicalDetails).map((key) => {
                          return (
                            <tr key={key}>
                              <th>{key}:</th>
                              <td>{priorityTechnicalDetails[key]}</td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                  {data.pimData &&
                  (data.pimData.cadfilestep ||
                    data.pimData.brochure ||
                    data.pimData.technicalspecs ||
                    data.pimData.software ||
                    data.pimData.installationandmaintenanceinstructionspdf) ? (
                    <p className="mt-5 font__bold">{getI18nText(microcopy, 'product.downloads')}</p>
                  ) : null}

                  {data.pimData ? <Downloads data={data} microcopy={microcopy} /> : null}
                </div>
              </div>
              <div className="row">
                <div className="col-15 col-md accordion product-details__product-data">
                  {data.pimData &&
                  (data.pimData.cadfilestep || data.pimData.cadenas_catalogue_path) ? (
                    <Cad
                      microcopy={microcopy}
                      cadFileStep={data.pimData.ResCadFileStep}
                      cadenasCataloguePath={data.pimData.cadenas_catalogue_path}
                      tacton_configurable={data.pimData.tacton_configurable}
                      tacton_reference={data.pimData.tacton_reference}
                      locale={props.pageContext.locale}
                      itemVarSet={data.pimData.varset}
                    />
                  ) : null}
                  <TechnicalDetails microcopy={microcopy} technicalDetails={technicalDetails} />
                  {data.pimData && data.pimData.schematicjpg ? (
                    <Schematics resSchematicJpg={data.pimData.schematicjpg} microcopy={microcopy} />
                  ) : null}
                </div>
                <ProductSidebox
                  isMobile={true}
                  loading={loading}
                  displayOptions={displayOptions}
                  product={product}
                  quantity={quantity}
                  description={productDescription}
                  setQuantityCallback={setQuantity}
                  openRequestAQuoteCallback={openRequestAQuoteModal}
                  contentfulProductData={data}
                />
              </div>
            </div>
            <ProductSidebox
              isMobile={false}
              loading={loading}
              displayOptions={displayOptions}
              product={product}
              quantity={quantity}
              description={productDescription}
              setQuantityCallback={setQuantity}
              openRequestAQuoteCallback={openRequestAQuoteModal}
              contentfulProductData={data}
            />
          </div>
          {data.pimData ? <Accessories microcopy={microcopy} data={data} /> : null}
          {showP65Warning ? <P65 microcopy={microcopy}></P65> : null}
        </div>
      </section>             
      <BaseModal
        isOpen={requestAQuoteIsOpen}
        closeModal={closeRequestAQuoteModal}
        productCode={data.partNumber}
        title={getI18nText(requestAQuoteMicrocopy, 'requestaquote.requestaquote')}
        component={ProductRequestAQuoteModal}
      />
      <Breadcrumb id={props.pageContext.navId} finalCrumb={data.partNumber} />
    </Layout>
  )
}
export default ProductPage

export function Head({ pageContext, data }) {
  const seoHrefLang = useSeoHrefLang({
    locale: pageContext.locale,
    altPaths: pageContext.altPaths,
  })
  const robots = useHideFromSpiders(pageContext)
  const [metaDescription] = useState(pageContext.metaDescription)
  const [pageTitle] = useState(GetPageTitle())

  function GetPageTitle() {
    const queryData = extract(data.allContentfulProduct, pageContext.locale)
    const pimDataDescription = replaceCharacterWithUnicodeCode(queryData?.pimData?.description)
    const description = pimDataDescription ? ` | ${pimDataDescription}` : ''
    return queryData.partNumber + description
  }

  return (
    <>
      {robots}
      {seoHrefLang}
      <Seo title={pageTitle} description={metaDescription} showTitleSuffix={false} />
    </>
  )
}

function extractAllItemsFromDictionary(srcItems) {
  let items = {}

  if (srcItems) {
    const keys = Object.keys(srcItems)

    for (let index = 0; index < keys.length; index++) {
      const name = keys[index]
      let value = srcItems[name]

      if (value) {
        value = Array.isArray(value) ? value.join(', ') : value
        items[name] = value
      }
    }

    if (Object.keys(items).length === 0) {
      items = null
    }
  } else {
    items = null
  }

  return items
}

function extractItemsFromDictionary(srcItems, maxItemCount) {
  let items = extractAllItemsFromDictionary(srcItems)

  if (items) {
    let extractedItems = {}
    const keys = Object.keys(items)
    let count = 0
    for (let index = 0; index < keys.length && count < maxItemCount; index++) {
      const name = keys[index]
      const value = items[name]
      extractedItems[name] = value
    }

    items = extractedItems
  }

  return items
}

function extract(result, locale) {
  for (let i = 0; i < result.nodes.length; i++) {
    let node = result.nodes[i]
    if (node.node_locale === locale) {
      return node
    }
  }

  return undefined
}

export const pageQuery = graphql`
  query ($partNumber: String, $locale: String) {
    allContentfulProduct(
      filter: { partNumber: { eq: $partNumber }, node_locale: { eq: $locale } }
    ) {
      nodes {
        id
        node_locale
        partNumber
        localImage {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
          }
        }
        pimData {
          accessories_accessories
          accessories_base_accessories
          accessories_connectors
          accessories_gauges
          accessories_flow_regulators
          accessories_fittings
          accessories_electrical_plugs
          accessories_mountings
          accessories_output_boards
          accessories_plates
          accessories_service_kits
          accessories_silencers
          accessories_similar
          accessories_solenoid_coils
          accessories_switches
          accessories_tubing
          accessories_iolink
          actuated_colour
          actuation
          actuator_variant
          adjusting_knob
          adjusting_screw
          adjustment
          air_consumption_flow_litresperminute_min_to_max_range
          air_flow_direction
          air_pilot_port_air_pilot_operated_types_only
          air_pilot_ports
          air_quality_output
          airflow_vacuum_nlmin
          ambient_temperature_temperature_celsius_min_to_max_range
          barrel
          basic_size
          body
          body_material
          body_style
          bonnet
          bowl
          bowl_capacity_volume_litre
          bowl_type
          brand
          bspp
          brochure
          bspt
          cable_length
          cable_length_length_meter
          cable_material
          cable_type
          cadenas_catalogue_path
          cadfilestep
          capacity_rating
          carriage
          certification__ip_rating
          certification__reach
          certification__rohs_compliant
          certification__weee
          coil_code
          collar
          collet
          colour
          connection
          connector_plug
          connector_type
          contact_resistance_resistance_milliohms
          control_signal
          coupling_type
          cover_strip
          cracking_pressure_pressure_bar
          current_consumption_current_milliamps
          current_current_amp
          cushion_length_length_millimetre
          cushioning
          cylinder_diameter_inch
          cylinder_diameter_metric_length_millimetre
          data_performance_b10_value_number_of_cycles
          data_performance_shelf_life
          description
          diameter_length_millimetre
          drain_connection
          effective_weight_range_mass_kilogram_min_to_max_range
          elastomers
          electrical_connection
          emc_standards
          end_covers
          eu_country_of_origin
          excelon_plus_series
          exhaust_flow_flow_litresperminute
          exhaust_port
          extended_stroke_length_length_millimetre
          failure_mode
          features
          female_bspp
          female_metric___bspp
          female_nptf
          femalemale
          filter_element
          filter_element_m_particlesize_micron
          finish
          flow_direction
          flow_flow_dm3sec
          flow_flow_litresperminute
          flow_flowcoefficient_cv
          flow_imperial_flow_scfm
          flow_kv_flow_kv
          flow_regulator
          flow_type
          fluid_temperature_imperial_temperature_fahrenheit_min_to_max_range
          fluid_temperature_temperature_celsius_min_to_max_range
          fog_type
          forces_force_newton
          forces_pressure_bar
          forward_flow_flowcoefficient_cv
          free_flow_factor_flow_dm3secbar
          gauge_port
          gland_size
          grabring
          guide_block
          guide_rod_diameter_length_millimetre
          guide_rods
          guiding_system
          hex
          hex_length_inches
          housing
          hysteresis_ratio_percent
          id
          id_tube_metric_length_millimetre
          indication
          induced_air_vacuum_nlmin
          initial_cushion_volume_volume_cubiccentimetre
          inlet_port
          inner_parts
          installationandmaintenanceinstructionspdf
          integral_flow_control
          interface
          iso_size
          israngesku
          kv_value_flow_cubicmeterperhour
          largeimage
          length_length_meter
          length_of_closed_spiral_length_millimetre
          liquid_level_control
          locking_force_force_newton
          long_description
          male_bspp
          male_bspt
          male_metric___bspp
          male_npt
          male_nptf
          manual_override
          manual_override_code
          materials
          maximum_current_current_amp
          maximum_energy_capacity_per_cycle_torque_newtonmetres
          maximum_energy_capacity_torque_newtonmetres
          maximum_flow_flow_dm3sec
          maximum_fluid_temperature_temperature_celsius
          maximum_inlet_pressure_imperial_pressure_psi
          maximum_inlet_pressure_pressure_bar
          maximum_number_of_stations
          maximum_operating_pressure_imperial_pressure_psi
          maximum_operating_pressure_imperial_pressure_psig
          maximum_operating_pressure_pressure_bar
          maximum_side_load_angle_angle_degrees
          maximum_stroke_metric_length_millimetre
          maximum_voltage_voltage_volt
          medium
          metric___bspp
          metric___bspt
          mil_spec
          minimum_bend_radius_length_millimetre
          minimum_operating_pressure_pressure_bar
          motor_flange
          motor_mounting
          mounting
          mounting_position
          mounting_style
          nominal_diameter
          nominal_diameter_length_millimetre
          nominal_size
          non_magnetic_or_magnetic
          npt
          number_of_convolutions
          number_of_masks
          number_of_pins
          number_of_tubes
          nuts_and_screws
          od_stem_inch
          od_stem_inch_length_inches
          od_stem_length_millimetre
          od_tube_inch
          od_tube_inlet_length_millimetre
          od_tube_length_millimetre
          od_tube_outlet_length_millimetre
          oil_removal_class
          operating_pressure_pressure_bar_min_to_max_range
          operating_pressure_psi_pressure_psi_min_to_max_range
          operating_temperature_f_temperature_fahrenheit_min_to_max_range
          operating_temperature_temperature_celsius_min_to_max_range
          operation
          operator
          operator_10
          operator_12
          operator_14
          orifice_metric
          oring
          outlet_port
          outlet_pressure_adjustment_imperial_pressure_psig_min_to_max_range
          outlet_pressure_adjustment_pressure_bar_min_to_max_range
          output
          output_pressure_imperial_pressure_psi_min_to_max_range
          output_pressure_pressure_bar_min_to_max_range
          over_pressure_pressure_bar
          over_pressure_psi_pressure_psi
          packing_and_supply_gross_weight_weight_kilogram
          packing_and_supply_packaging_diameter_length_millimetre
          packing_and_supply_packaging_height_length_millimetre
          packing_and_supply_packaging_length_length_millimetre
          packing_and_supply_packaging_material
          packing_and_supply_packaging_width_length_millimetre
          packing_and_supply_product_diameter_length_millimetre
          packing_and_supply_product_height_length_millimetre
          packing_and_supply_product_length_length_millimetre
          packing_and_supply_product_weight_weight_kilogram
          packing_and_supply_product_width_length_millimetre
          packing_and_supply_specified_supplier_packaging
          packing_and_supply_unit_of_measure
          partially_replaced_part
          particle_removal_particlesize_micron
          pilot
          pilot_exhaust
          pilot_medium
          pilot_pressure_imperial_pressure_psi_min_to_max_range
          pilot_pressure_pressure_bar_min_to_max_range
          pinion_diameter_length_millimetre
          piston
          piston_head_seals
          piston_rod
          piston_rod_diameter_inch
          piston_rod_diameter_metric_length_millimetre
          piston_rod_material
          piston_rod_seals
          piston_rod_thread
          piston_rod_thread_type
          port_connections
          port_orientation
          port_orientation__mounting_style
          port_regulation
          port_size
          port_type
          porting
          power_consumption_power_watt
          power_inrushhold_power_watt
          power_power_watt
          pressure_drop_at_shut_off_flow_pressure_bar
          pressure_range_pressure_bar_min_to_max_range
          pressure_range_psi_pressure_psi_min_to_max_range
          product_style
          protection_class
          pyrex_sight_glass
          quick_fill_nipple
          range
          rectified_coil
          regulator_type
          release_sleeve_and_backing_ring
          relief_flow_rate_flow_litresperminute
          relief_port
          relievingnonrelieving
          repeat_accuracy_ratio_percent
          replaced_flag
          response_time_time_millisecond
          retrieve_price
          return_force_of_spring_force_newton
          return_force_of_spring_range_force_newton_min_to_max_range
          reverse_flow_flowcoefficient_cv
          rod_bearing
          rod_reset_time_time_seconds
          rod_type
          rohscertificatepdf
          rotation_angle
          rotation_tolerance
          scale_bar_pressure_bar_min_to_max_range
          scale_mpa_pressure_mpa_min_to_max_range
          scale_psi_pressure_psi_min_to_max_range
          schematicjpg
          schematicsvg
          seal_material
          sealing_strip
          sealing_washer
          seals
          seals___orings
          seat_seal
          sensitivity_pressure_mbar
          series
          service_life_indicator
          shape
          shock_resistance_weight_gram
          shut_off_flow_rate_flow_dm3sec
          shut_off_valve
          silencer
          socket
          software
          solenoid_connector
          solenoid_operation
          solenoid_power_power_watt
          solenoid_protection_class
          sound_level_soundlevel_db
          spindle_pitch_metric
          spool
          spool___sleeve
          spool_technology
          springs
          sprung_in_or_sprung_out
          standard
          start_point_flow_dm3sec
          start_point_imperial_flow_scfm
          statementofconformitypdf
          stroke_length_inch
          stroke_length_metric_length_millimetre
          stroke_travel_length_millimetre
          subbase_modules
          supply_flow_flow_litresperminute
          supply_voltage
          surge_suppression
          switch_type
          switching_current_current_milliamps
          switching_frequency_frequency_perminute
          switching_function
          switching_power_power_watt
          switching_pressure_difference_imperial_pressure_psi_min_to_max_range
          switching_pressure_difference_pressure_bar_min_to_max_range
          switching_time__off_time_millisecond
          switching_time__on_time_millisecond
          switching_time_time_millisecond
          switching_voltage_voltage_volt
          technicalspecs
          theoretical_force_force_newton
          thread
          thread_form
          thread_sealant
          thread_sizes
          thread_type
          threaded_bodies
          thumbnail
          top_cover
          torque_torque_newtonmetres
          tube_sizes_length_millimetre
          tube_types
          type_of_pressure
          unactuated_colour
          vacuum
          vacuum_rating
          vacuum_rating_pressure_mmhg
          valve_function
          valve_operation
          valve_options
          valve_size_length_millimetre
          valve_variant_actuation
          vane_type
          videourl
          voltage
          wiper
          tacton_configurable
          tacton_reference
          zoomedimage
          barrel
          cushioning
          cv_body_flowcoefficient_cv
          flow_imperial_flow_scfm
          fluid_temperature_imperial_temperature_fahrenheit_min_to_max_range
          guiding_system
          non_magnetic_or_magnetic
          operating_pressure_psi_pressure_psi_min_to_max_range
          outlet_pressure_adjustment_imperial_pressure_psig_min_to_max_range
          scale_bar_pressure_bar_min_to_max_range
          scale_mpa_pressure_mpa_min_to_max_range
          scale_psi_pressure_psi_min_to_max_range
          stroke_length_inch
          theoretical_force_force_newton
          drain_type
          function
          varset
        }
      }
    }
  }
`
